import { Component, OnInit, Output, EventEmitter, ViewChild, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';
import { DataService } from '../../../../../../services/dashboard/data.service';
import { DmtUtilityService } from '../../../../../../services/dashboard/dmt-utility.service';
import { CommonModule } from '@angular/common';
import { TimelineEvolutionRegionalAccess } from '../../../../../../shared/interfaces/nt-EvolutionRegionalAccess.interface';
import { NtChartRegionInterface } from '../../../../../../shared/interfaces/nt-chart-region.interface';
import { forkJoin } from 'rxjs';
import { TimelineEvolutionRegionalMarket30Days } from '../../../../../../shared/interfaces/nt-EvolutionRegionalMarket30days.interface';
import { ModalEvolutionRegionalMarketComponent } from '../modal-evolution-regional-market/modal-evolution-regional-market.component';


@Component({
  selector: 'app-evolution-regional-market-30-days',
  standalone: true,
  imports: [CommonModule, ModalEvolutionRegionalMarketComponent],
  templateUrl: './evolution-regional-market-30-days.component.html',
  styleUrl: './evolution-regional-market-30-days.component.css'
})
export class EvolutionRegionalMarket30DaysComponent implements OnInit, AfterViewInit, OnDestroy{
  @Output() isTwoRowLayout = new EventEmitter<boolean>();
  @ViewChild('carouselEvolutionRegionalAccess') carousel!: ElementRef;

  public medicineId: number = 0;
  public companyId: number = 0;
  public countryId: number = 1;
  public regionId: number = 0;
  public regionName?: string | null;
  stepSelected: string = '';
  hospitalOnekey: string = '';
  hasForecastData: boolean = false;
  hasActualData: boolean = false;
  isTwoRow: boolean = false;
  isModalOpen: boolean = false;
  blockToSend: any;
  currentIndex: number = 0;
  maxIndex: number = 0;
  private resizeListener: any;

  public chartData: NtChartRegionInterface = {
    labels: [],
    actual: [],
    real: [],
    target: [],
  };

  timelineEvolution: TimelineEvolutionRegionalMarket30Days[] = [];
  isLoadingTimeline: boolean = true;

  constructor(
    private dataService: DataService,
    private us: DmtUtilityService,
  ) {}

  ngOnInit(): void {
    this.isLoadingTimeline = true;
    this.getSessionVariables();
    this.getRegionalAccessEvolution();
  }

  ngAfterViewInit(): void {
  this.resizeListener = this.updateMaxIndex.bind(this);
  window.addEventListener('resize', this.resizeListener);
  }

ngOnDestroy(): void {
  if (this.resizeListener) {
    window.removeEventListener('resize', this.resizeListener);
  }
}


  getRegionalAccessEvolution(): void {
    this.dataService
      .getOpenToMarketEvolution30Days(this.companyId, this.medicineId, this.countryId)
      .subscribe({
        next: (data) => {
          this.handleTimelineData(data);
        },
        error: (err) => {
          console.error('Error fetching Evolution Open to Market 30 Days data:', err);
          // Same as an empty response: placeholder timeline + "Non ci sono dati da mostrare"
          this.handleTimelineData([]);
        },
      });
  }

  private handleTimelineData(data: TimelineEvolutionRegionalMarket30Days[]): void {
    // we can use data=[] to simulate the empty data scenario
    //data=[];

    // fill missing months in the timeline (or create placeholder if empty)
    if (!data || data.length === 0) {
      this.timelineEvolution = this.createPlaceholderTimeline();
    } else {
      this.timelineEvolution = this.fillMissingMonths(data);
      this.updateMaxIndex();
    }

    this.hasForecastData = this.timelineEvolution.some(
      (item) => item.forecast && item.forecast.length > 0,
    );

    this.hasActualData = this.timelineEvolution.some(
      (item) => item.actual && item.actual.length > 0,
    );

    let hasBothSeries = this.hasForecastData && this.hasActualData;

    this.isTwoRowLayout.emit(hasBothSeries);
    this.isTwoRow = hasBothSeries;
    this.isLoadingTimeline = false;
  }

  private monthIndexOf(datePoint: string): number {
    const m = String(datePoint).match(/(\d+)/);
    return m ? Number(m[1]) : 0;
  }

  // "Month 3" -> "Mese 3", same label used in open-to-market-curve-30-days
  private toMeseLabel(index: number): string {
    return 'Mese ' + index;
  }


  // true if the month has at least one period date (forecast or actual) to show in the tooltip
  hasPeriodDates(item: TimelineEvolutionRegionalMarket30Days): boolean {
    return !!(item.forecastStartPeriodDate || item.forecastEndPeriodDate || item.actualStartPeriodDate || item.actualEndPeriodDate);
  }

  // "2026-03-18" -> "18/03/26"
  formatPeriodDate(date: string): string {
    const m = String(date || '').match(/^(\d{4})-?(\d{2})-?(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1].slice(2)}` : '-';
  }

  // ---- Period dates for the inserted (empty) periods: same calendar-month rules as
  // open-to-market-curve-30-days. Local dates at midnight, no UTC conversion. ----

  // "yyyy-MM-dd" (optionally followed by a time) -> local date, or null if not valid.
  private parseYmd(dateStr: string | null | undefined): Date | null {
    const m = String(dateStr ?? '').trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return null;
    const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return date.getMonth() === Number(m[2]) - 1 ? date : null;
  }

  private toYmd(date: Date): string {
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${mm}-${dd}`;
  }

  // Step 0 + n calendar months, always anchored on Step 0 and clamped to the end of the month.
  private addMonthsFromStart(start: Date, n: number): Date {
    const lastDay = new Date(start.getFullYear(), start.getMonth() + n + 1, 0).getDate();
    return new Date(start.getFullYear(), start.getMonth() + n, Math.min(start.getDate(), lastDay));
  }

  // Month 1 = [Step 0, Step 0 + 1 month], Month N = [Step 0 + (N-1) months + 1 day, Step 0 + N months].
  // Month 0 and a missing Step 0 -> no range (empty strings, so the tooltip hides that row).
  private periodRange(startDate: string | null | undefined, n: number): { start: string; end: string } {
    const start = this.parseYmd(startDate);
    if (!start || n < 1) return { start: '', end: '' };
    const end = this.addMonthsFromStart(start, n);
    if (n === 1) return { start: this.toYmd(start), end: this.toYmd(end) };
    const prevEnd = this.addMonthsFromStart(start, n - 1);
    const periodStart = new Date(prevEnd.getFullYear(), prevEnd.getMonth(), prevEnd.getDate() + 1);
    return { start: this.toYmd(periodStart), end: this.toYmd(end) };
  }


  private createPlaceholderTimeline():TimelineEvolutionRegionalMarket30Days[] {
    const placeholder: TimelineEvolutionRegionalMarket30Days[] = [];

    for (let i = 0; i < 8; i++) {
      placeholder.push({
        datePoint: this.toMeseLabel(i),
        forecast: [],
        actual: [],
        forecastStartDate: '',
        actualStartDate: '',
        forecastStartPeriodDate: '',
        forecastEndPeriodDate: '',
        actualStartPeriodDate: '',
        actualEndPeriodDate: ''
      });
    }

    return placeholder;
  }


  private fillMissingMonths(data: TimelineEvolutionRegionalMarket30Days[]): TimelineEvolutionRegionalMarket30Days[] {
    if (!data || data.length === 0) {
      return [];
    }

    // Sort data by period index ("Month N")
    const sortedData = [...data].sort((a, b) => this.monthIndexOf(a.datePoint) - this.monthIndexOf(b.datePoint));

    // Step 0 of each series (same on every item): the anchors for the inserted periods.
    // Forecast and Actual are independent; a missing anchor leaves that series without dates.
    const forecastStartDate = sortedData.find(item => item.forecastStartDate)?.forecastStartDate || '';
    const actualStartDate = sortedData.find(item => item.actualStartDate)?.actualStartDate || '';

    const filledTimeline: TimelineEvolutionRegionalMarket30Days[] = [];

    for (let i = 0; i < sortedData.length; i++) {
      const current = this.monthIndexOf(sortedData[i].datePoint);

      // Add current item with the "Mese N" label (backend period dates kept as they are).
      // Month 0 is the starting point: no period range in the tooltip.
      filledTimeline.push(current === 0
        ? {
            ...sortedData[i],
            datePoint: this.toMeseLabel(current),
            forecastStartPeriodDate: '',
            forecastEndPeriodDate: '',
            actualStartPeriodDate: '',
            actualEndPeriodDate: ''
          }
        : { ...sortedData[i], datePoint: this.toMeseLabel(current) });

      // If there's a next item, add the missing periods in between,
      // with each series' period dates calculated from its own Step 0
      if (i < sortedData.length - 1) {
        const next = this.monthIndexOf(sortedData[i + 1].datePoint);
        for (let missing = current + 1; missing < next; missing++) {
          const forecastPeriod = this.periodRange(forecastStartDate, missing);
          const actualPeriod = this.periodRange(actualStartDate, missing);
          filledTimeline.push({
            datePoint: this.toMeseLabel(missing),
            forecast: [],
            actual: [],
            forecastStartDate: forecastStartDate,
            actualStartDate: actualStartDate,
            forecastStartPeriodDate: forecastPeriod.start,
            forecastEndPeriodDate: forecastPeriod.end,
            actualStartPeriodDate: actualPeriod.start,
            actualEndPeriodDate: actualPeriod.end
          });
        }
      }
    }

    return filledTimeline;
  }

   private updateMaxIndex(): void {
    const screenWidth = window.innerWidth;
    let maxCards = 6;
    
    if (screenWidth >= 1516) {
      maxCards = 8;
    } else if (screenWidth >= 1280) {
      maxCards = 7;
    } else {
      maxCards = 6;
    }
    
    this.maxIndex = Math.max(0, this.timelineEvolution.length - maxCards);
    
    // if currentIndex is outside of range, step back to maxIndex
    if (this.currentIndex > this.maxIndex) {
      this.currentIndex = this.maxIndex;
      this.scrollToCurrent();
    }
  }


 next() {
    if (this.currentIndex < this.maxIndex) {
      this.currentIndex++;
      this.scrollToCurrent();
    }
  }

  prev() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.scrollToCurrent();
    }
  }
  

private scrollToCurrent() {
  if (this.carousel?.nativeElement) {
    const card = this.carousel.nativeElement.querySelector('.pointInTimelineMarket');
    if (card) {
      // we read the size of each card (in this case the entire datepoint) and we multiply it 
      // with the index number and assign the value to the "left" property
      const cardWidth = card.offsetWidth       
      this.carousel.nativeElement.scrollTo({
        left: this.currentIndex * cardWidth,
        behavior: 'smooth',
      });
    }
  }
}


  getSessionVariables() {
    this.regionName = sessionStorage.getItem('regionName') || '';
    this.medicineId = this.us.getSessionVariableNumber('medicineId', 0);
    this.countryId = this.us.getSessionVariableNumber('countryId');
    this.regionId = this.us.getSessionVariableNumber('regionId');
    this.companyId = this.us.getSessionVariableNumber('companyId', 0);
    this.stepSelected = sessionStorage.getItem('stepSelected') || '';
    this.hospitalOnekey = sessionStorage.getItem('hospitalOnekey') || '';
  }

  handleRegionClick(datePoint: string, isActual: boolean, region: any): void {
    this.blockToSend = {
      datePoint: datePoint,
      isActual: isActual,
      region: region,
    };
    this.handelModalOpen(true);
  }

  handelModalOpen(status: boolean) {
    this.isModalOpen = status;
  }
}
